// IndexedDB-backed persistence. We persist the *state shape* (pages, metadata,
// stamps, redactions, options, theme) but NOT the heavyweight per-doc byte
// buffers. Buffers are persisted under a separate key keyed by docId so the
// state record stays compact and writes stay fast.

import { get as idbGet, set as idbSet, del as idbDel, keys as idbKeys } from 'idb-keyval'

const STATE_KEY = 'workbench:state:v1'
const BYTES_PREFIX = 'workbench:bytes:'

function trim(state) {
  return {
    documents: (state.documents || []).map((d) => ({
      // Bytes excluded — saved separately.
      id: d.id,
      originalName: d.originalName,
      size: d.size,
      pages: d.pages,
      formFields: d.formFields,
      formScanned: d.formScanned,
      sourceKind: d.sourceKind || 'pdf',
    })),
    pageOrder: state.pageOrder,
    selectedPageId: state.selectedPageId,
    selectedDocumentId: state.selectedDocumentId,
    splitGroups: state.splitGroups,
    exportOptions: state.exportOptions,
    stampSettings: state.stampSettings,
    annotationSettings: state.annotationSettings,
    signatures: state.signatures,
    formTemplates: state.formTemplates,
    theme: state.theme,
    ocrLanguage: state.ocrLanguage,
  }
}

export async function persistState(state) {
  try {
    await idbSet(STATE_KEY, trim(state))
    for (const d of state.documents || []) {
      if (d.bytes) {
        await idbSet(`${BYTES_PREFIX}${d.id}`, d.bytes)
      }
    }
  } catch (err) {
    // Silently ignore quota / permission issues — persistence is best-effort.
    console.warn('persistState failed', err)
  }
}

export async function loadPersistedState() {
  try {
    const saved = await idbGet(STATE_KEY)
    if (!saved) return null
    const docs = []
    for (const d of saved.documents || []) {
      const bytes = await idbGet(`${BYTES_PREFIX}${d.id}`)
      if (!bytes) continue // dropped because bytes are missing
      docs.push({ ...d, bytes })
    }
    return { ...saved, documents: docs }
  } catch (err) {
    console.warn('loadPersistedState failed', err)
    return null
  }
}

export async function clearPersisted() {
  try {
    const all = await idbKeys()
    await Promise.all(
      all
        .filter(
          (k) =>
            typeof k === 'string' &&
            (k === STATE_KEY || k.startsWith(BYTES_PREFIX))
        )
        .map((k) => idbDel(k))
    )
  } catch (_) {
    // ignore
  }
}
